import ClinicalAudiogram from "@/components/ClinicalAudiogram";
import HearingKnowledge from "@/pages/HearingKnowledge";
import HearenLanding from "@/pages/HearenLanding";
import AdminNativeAudioLab from './pages/AdminNativeAudioLab';
import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { FullscreenProvider, useFullscreen } from "@/contexts/FullscreenContext";
import { NowPlayingProvider } from "@/contexts/NowPlayingContext";
import { API_BASE_URL } from "@/services/httpClient";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import TabBar from "@/components/TabBar";
import MiniPlayer from "@/components/MiniPlayer";
import { Toaster } from "@/components/ui/sonner"; // BUILD 65: Toast notifications
// TopBar supprimé - Navigation centralisée dans More/Profile
import HomeNew from "@/pages/HomeNew"; // V1 ACTIVE HOME - SEULE Home rendue
import HearenIntroIdentity, { shouldShowHearenIntro } from './components/HearenIntroIdentity';
import Profile from "@/pages/Profile";
import SOS from "@/pages/SOS";
import SOSCalm from "@/pages/SOSCalm";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import Tests from "@/pages/Tests";
import AudiometricTest from "@/pages/AudiometricTest";
import TinnitusTest from "@/pages/TinnitusTest";
import TherapyPlayer from "@/pages/TherapyPlayer";
import History from "@/pages/History";
import Progress from "@/pages/Progress";
import CustomSounds from "@/pages/CustomSounds";
import AdminSounds from "@/pages/AdminSounds";
import AdminDisplayNames from "@/pages/AdminDisplayNames";
import AdminVoiceMessages from "@/pages/AdminVoiceMessages";
// V1.1.1: Radio removed - AdminRadioSounds disabled
// import AdminRadioSounds from "@/pages/AdminRadioSounds";
import AdminHome from "@/pages/AdminHome";
import AdminDebugNetwork from "@/pages/AdminDebugNetwork";
import AdminBackups from "@/pages/AdminBackups";
import AdminHomeLab from "@/pages/AdminHomeLab";
import VoiceTest from "@/pages/VoiceTest";
import Guide from "@/pages/Guide";
import Assistant from "@/pages/Assistant";
import Methodology from "@/pages/Methodology";
import Playlists from "@/pages/Playlists";
import Favorites from "@/pages/Favorites";
// V1.1.1: Radio removed
// import RadioHearen from "@/pages/RadioHearen";
import TalkToHearen from "@/pages/TalkToHearen";
import Premium from "@/pages/Premium";
import TherapyTimerDemo from "@/pages/TherapyTimerDemo";
import Onboarding from "@/pages/Onboarding";
import GetStarted from "@/pages/GetStarted";
import Coach from "@/pages/Coach";
import CoachCheckin from "@/pages/CoachCheckin";
import LabSettings from "@/pages/lab/LabSettings";
import LabPlan from "@/pages/lab/LabPlan";
import PitchMatch from "@/pages/lab/PitchMatch";
import BreathingPremium from "@/pages/BreathingPremium";
import BreathingExercise from "@/pages/BreathingExercise";
import BreathingFree from "@/pages/BreathingFree";
import NightMode from "@/pages/NightMode";
import PremiumUpsellBreathing from "@/pages/PremiumUpsellBreathing";
import JourneyIntro from "@/pages/JourneyIntro";
import JourneyBreathingCycle from "@/pages/JourneyBreathingCycle";
import JourneyRespirationDiscovery from "@/pages/JourneyRespirationDiscovery";
import JourneyAudioTestPreview from "@/pages/JourneyAudioTestPreview";
import JourneyTinnitusTestPreview from "@/pages/JourneyTinnitusTestPreview";
import JourneyTherapeuticTestPreview from "@/pages/JourneyTherapeuticTestPreview";
import JourneyRespirationPreview from "@/pages/JourneyRespirationPreview";
import JourneyTherapyPreview from "@/pages/JourneyTherapyPreview";
import JourneyProgressPreview from "@/pages/JourneyProgressPreview";
import JourneyFinal from "@/pages/JourneyFinal";
import JourneyOrientation from "@/pages/JourneyOrientation";
import JourneyAudioQuality from "@/pages/JourneyAudioQuality";
import translations from "@/i18n/translations";
import userManager from "@/services/userManager";
import { installGlobalUnlockListener } from "@/services/iosAudioHelper";
import { Globe } from "lucide-react";
import ScrollToTop from "@/components/ScrollToTop";
// import VersionMarker from "@/components/VersionMarker";

// iOS/Capacitor Fix: Default redirect component for unknown routes
// Handles /index.html, empty paths, and any unmatched routes
const DefaultRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Log for debugging iOS issues
    console.log('[DefaultRedirect] Unknown path detected:', location.pathname);
    console.log('[DefaultRedirect] Auth state:', { isAuthenticated, loading });
    
    if (!loading) {
      // Redirect based on auth state
      if (isAuthenticated) {
        console.log('[DefaultRedirect] Redirecting to /home');
        navigate('/', { replace: true });
      } else {
        console.log('[DefaultRedirect] Redirecting to /login');
        navigate('/login', { replace: true });
      }
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);
  
  // Show loading while determining redirect
  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-white/60 text-sm">Chargement...</p>
      </div>
    </div>
  );
};

/**
 * ProgressWithCheckin - Simple wrapper for Progress page
 * The daily check-in is now handled entirely in the Coach page
 */
const ProgressWithCheckin = ({ t }) => {
  return <Progress t={t} />;
};

// Inner component that can use AuthContext
function AppContent() {
  const { user, updateUserLanguage } = useAuth();
  // V1.3: Use centralized language management with persistence
  const [language, setLanguage] = useState(() => {
    // Initialize from persisted preference (hearen_lang)
    try {
      const stored = localStorage.getItem('hearen_lang');
      if (stored === 'en' || stored === 'fr') {
        return stored;
      }
      // Migration from old key
      const oldStored = localStorage.getItem('hearen-language');
      if (oldStored) {
        const normalized = oldStored.toLowerCase().startsWith('en') ? 'en' : 'fr';
        localStorage.setItem('hearen_lang', normalized);
        localStorage.removeItem('hearen-language');
        return normalized;
      }
      // Fallback to browser language
      const browserLang = navigator.language || 'fr';
      return browserLang.toLowerCase().startsWith('en') ? 'en' : 'fr';
    } catch (e) {
      return 'fr';
    }
  });
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  // V1.3: Do NOT sync from user.language - localStorage is the source of truth
  // This ensures the user's explicit choice is preserved across sessions

  // V1.4: Install iOS audio unlock listener on first mount
  useEffect(() => {
    // Install global unlock listener for iOS Safari
    // This captures the first user gesture to unlock audio playback
    installGlobalUnlockListener();
  }, []); // Only run once on mount

  // V1.1: Listen to languageChanged events (reactive language switching)
  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLang = event.detail?.language || localStorage.getItem('hearen_lang') || 'fr';
      const normalized = newLang.toLowerCase().startsWith('en') ? 'en' : 'fr';
      if (normalized !== language) {
        setLanguage(normalized);
        console.log(`🌍 [App] Language changed reactively to: ${normalized}`);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, [language]);

  const t = translations[language];

  const changeLanguage = async (lang) => {
    // V1.3: Normalize and persist
    const normalized = lang.toLowerCase().startsWith('en') ? 'en' : 'fr';
    setLanguage(normalized);
    setIsLanguageMenuOpen(false);
    
    // Store in localStorage (source of truth)
    localStorage.setItem('hearen_lang', normalized);
    console.log(`🌍 [App] Language preference saved: ${normalized}`);
    
    // V1.4: Update user profile in backend using correct endpoint
    try {
      if (user?.id) {
        // Use updateUserLanguage from AuthContext which calls /api/me/lang
        updateUserLanguage(normalized);
      }
    } catch (error) {
      console.error('Error updating language:', error);
    }
    
    // Dispatch custom event for components to listen
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: normalized } }));
    
    console.log(`🌍 Language changed to: ${normalized}`);
    
    // V1.1: No more forced reload - components use useLanguage hook for reactive updates
  };

  // Component to conditionally render TabBar and TopBar
  const AppLayout = ({ children, t }) => {
    const location = useLocation();
    const { isFullscreen } = useFullscreen();
    
    // Hide TabBar and TopBar on login, register, onboarding, get-started pages, Journey pages, and fullscreen mode
    const journeyPaths = [
      '/journey-intro',
      '/journey-breathing-cycle',
      '/journey-respiration-preview',
      '/journey-audio-test',
      '/journey-tinnitus-test',
      '/journey-therapeutic-test',
      '/journey-respiration',
      '/journey-therapy',
      '/journey-progress-preview',
      '/journey-final'
    ];
    const hideNavigation = 
      ['/login', '/register', '/forgot-password', '/reset-password', '/onboarding', '/get-started', '/sos-calm', '/night-mode', '/therapy'].includes(location.pathname) ||
      location.pathname.startsWith('/therapy') ||
      journeyPaths.includes(location.pathname) ||
      isFullscreen;
    
    
    return (
      <>
        {children}
        {/* TopBar supprimé - Navigation centralisée dans More/Profile */}
        {!hideNavigation && <TabBar t={t} />}
        {/* Mini player — visible on all non-therapy screens when audio is active */}
        {!location.pathname.startsWith('/therapy') && <MiniPlayer />}
      </>
    );
  };
    return (
  <>
    {/* Language selector dropdown menu */}
    {isLanguageMenuOpen && (
      <>
            {/* Backdrop */}
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10000
              }}
              onClick={() => setIsLanguageMenuOpen(false)}
            />
            
            {/* Language Menu */}
            <div 
              style={{
                position: 'fixed',
                top: '5rem',
                left: '1rem',
                width: '10rem',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                zIndex: 10001
              }}
            >
              <button
                onClick={() => changeLanguage('fr')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTopLeftRadius: '0.5rem',
                  borderTopRightRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: language === 'fr' ? '#eff6ff' : 'white',
                  color: language === 'fr' ? '#2563eb' : '#374151',
                  fontWeight: language === 'fr' ? '600' : '400'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = language === 'fr' ? '#eff6ff' : '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = language === 'fr' ? '#eff6ff' : 'white'}
                data-testid="lang-fr"
              >
                <span>Français</span>
                {language === 'fr' && (
                  <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => changeLanguage('en')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottomLeftRadius: '0.5rem',
                  borderBottomRightRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: language === 'en' ? '#eff6ff' : 'white',
                  color: language === 'en' ? '#2563eb' : '#374151',
                  fontWeight: language === 'en' ? '600' : '400'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = language === 'en' ? '#eff6ff' : '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = language === 'en' ? '#eff6ff' : 'white'}
                data-testid="lang-en"
              >
                <span>English</span>
                {language === 'en' && (
                  <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </>
        )}
        
        <ScrollToTop />
        
        <AppLayout t={t}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login t={t} />} />
            <Route path="/register" element={<Register t={t} />} />
            <Route
              path="/reset-password"
              element={
                <ResetPassword
                  t={t}
                  language={language}
                  onLanguageChange={changeLanguage}
                />
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword t={t} />} />

            {/* Hearen Landing (TEST) */}
            <Route path="/landing" element={<HearenLanding />} />

            {/* Onboarding & Get Started */}
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding t={t} /></ProtectedRoute>} />
            <Route path="/get-started" element={<ProtectedRoute><GetStarted t={t} /></ProtectedRoute>} />
            
            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute><HomeNew t={t} /></ProtectedRoute>} />
            <Route path="/home" element={<ProtectedRoute><HomeNew t={t} /></ProtectedRoute>} />
            <Route path="/tests" element={<ProtectedRoute><ErrorBoundary><Tests t={t} /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/audiometric-test" element={<ProtectedRoute><ErrorBoundary><AudiometricTest t={t} /></ErrorBoundary></ProtectedRoute>} />
            <Route
              path="/clinical-audiogram"
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <ClinicalAudiogram t={t} />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hearing-knowledge"
              element={
                <ProtectedRoute>
                  <HearingKnowledge t={t} />
                </ProtectedRoute>
              }
            />
            <Route path="/tinnitus-test" element={<ProtectedRoute><ErrorBoundary><TinnitusTest t={t} /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/therapy" element={<ProtectedRoute><ErrorBoundary><TherapyPlayer t={t} /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/playlists" element={<ProtectedRoute><Playlists t={t} /></ProtectedRoute>} />
            <Route path="/favorites" element={<ProtectedRoute><Favorites t={t} /></ProtectedRoute>} />
            {/* V1.1.1: Radio removed - redirect to Soundscapes */}
            <Route path="/radio" element={<Navigate to="/therapy?mode=soundscapes" replace />} />
            {/* TEMPORARY DISABLED - Speak to Hearen feature on hold */}
            {/* <Route path="/talk-to-hearen" element={<ProtectedRoute><TalkToHearen t={t} /></ProtectedRoute>} /> */}
            
            {/* Redirect old talk-to-hearen route to home */}
            <Route path="/talk-to-hearen" element={<Navigate to="/home" replace />} />
            <Route path="/premium" element={<ProtectedRoute><Premium t={t} /></ProtectedRoute>} />
            <Route path="/therapy-timer-demo" element={<ProtectedRoute><TherapyTimerDemo t={t} /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><History t={t} /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute><ProgressWithCheckin t={t} /></ProtectedRoute>} />
            <Route path="/progress/plan" element={<ProtectedRoute><LabPlan t={t} /></ProtectedRoute>} />
            <Route path="/coach/checkin" element={<ProtectedRoute><CoachCheckin t={t} /></ProtectedRoute>} />
            <Route path="/coach" element={<ProtectedRoute><Coach t={t} /></ProtectedRoute>} />
            <Route path="/settings/lab" element={<ProtectedRoute><LabSettings t={t} /></ProtectedRoute>} />
            <Route path="/lab/pitch-match" element={<ProtectedRoute><PitchMatch t={t} /></ProtectedRoute>} />
            <Route path="/custom-sounds" element={<ProtectedRoute><CustomSounds t={t} /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminHome t={t} /></ProtectedRoute>} />
            <Route path="/admin/sounds" element={<ProtectedRoute><AdminSounds t={t} /></ProtectedRoute>} />
            <Route path="/admin/display-names" element={<ProtectedRoute><AdminDisplayNames t={t} /></ProtectedRoute>} />
            <Route path="/admin/voice-messages" element={<ProtectedRoute><AdminVoiceMessages t={t} /></ProtectedRoute>} />
            {/* V1.1.1: Radio admin removed */}
            <Route path="/admin/radio-sounds" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/debug/network" element={<ProtectedRoute><AdminDebugNetwork t={t} /></ProtectedRoute>} />
            <Route path="/admin/backups" element={<ProtectedRoute><AdminBackups /></ProtectedRoute>} />
            <Route path="/admin/home-lab" element={<ProtectedRoute><AdminHomeLab /></ProtectedRoute>} />
            <Route path="/admin/native-audio-lab" element={<ProtectedRoute><AdminNativeAudioLab /></ProtectedRoute>} />
            <Route path="/voice-test" element={<ProtectedRoute><VoiceTest t={t} /></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute><Guide t={t} /></ProtectedRoute>} />
            <Route path="/assistant" element={<ProtectedRoute><Assistant t={t} /></ProtectedRoute>} />
            <Route path="/methodology" element={<ProtectedRoute><Methodology t={t} /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile t={t} onLanguageChange={changeLanguage} /></ProtectedRoute>} />
            <Route path="/sos" element={<ProtectedRoute><SOS t={t} /></ProtectedRoute>} />
            <Route path="/sos-calm" element={<ProtectedRoute><SOSCalm t={t} /></ProtectedRoute>} />
            <Route path="/breathing-premium" element={<ProtectedRoute><BreathingPremium t={t} /></ProtectedRoute>} />
            <Route path="/breathing-exercise/:id" element={<ProtectedRoute><BreathingExercise t={t} /></ProtectedRoute>} />
            <Route path="/breathing-free" element={<ProtectedRoute><BreathingFree t={t} /></ProtectedRoute>} />
            <Route path="/night-mode" element={<ProtectedRoute><ErrorBoundary><NightMode /></ErrorBoundary></ProtectedRoute>} />
            
            {/* Journey routes - Parcours guidé premium (10 écrans) */}
            <Route path="/journey-intro" element={<ProtectedRoute><JourneyIntro /></ProtectedRoute>} />
            <Route path="/journey-breathing-cycle" element={<ProtectedRoute><JourneyBreathingCycle /></ProtectedRoute>} />
            <Route path="/journey-respiration-preview" element={<ProtectedRoute><JourneyRespirationDiscovery /></ProtectedRoute>} />
            <Route path="/journey-audio-test" element={<ProtectedRoute><JourneyAudioTestPreview /></ProtectedRoute>} />
            <Route path="/journey-tinnitus-test" element={<ProtectedRoute><JourneyTinnitusTestPreview /></ProtectedRoute>} />
            <Route path="/journey-therapeutic-test" element={<ProtectedRoute><JourneyTherapeuticTestPreview /></ProtectedRoute>} />
            <Route path="/journey-audio-quality" element={<ProtectedRoute><JourneyAudioQuality /></ProtectedRoute>} />
            <Route path="/journey-respiration" element={<ProtectedRoute><JourneyRespirationPreview /></ProtectedRoute>} />
            <Route path="/journey-therapy" element={<ProtectedRoute><JourneyTherapyPreview /></ProtectedRoute>} />
            <Route path="/journey-progress-preview" element={<ProtectedRoute><JourneyProgressPreview /></ProtectedRoute>} />
            <Route path="/journey-final" element={<ProtectedRoute><JourneyFinal /></ProtectedRoute>} />
            <Route path="/journey-orientation" element={<ProtectedRoute><JourneyOrientation /></ProtectedRoute>} />
            
            {/* iOS/Capacitor Fix: Handle common edge case paths */}
            <Route path="/index.html" element={<DefaultRedirect />} />
            <Route path="/index" element={<DefaultRedirect />} />
            
            {/* Catch-all route: redirect unknown paths to home or login */}
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
            </AppLayout>
        </>
      );
  }

// iOS/Capacitor: Initial route fixer component
// Ensures the app navigates to the correct initial route on Capacitor launch
const CapacitorInitialRouteFix = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, token, networkError } = useAuth();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Only run once on mount, after auth is loaded
    if (hasInitializedRef.current || loading) return;
    hasInitializedRef.current = true;
    
    const isCapacitor = window.Capacitor !== undefined;
    if (!isCapacitor) return;
    
    const currentPath = location.pathname;
    const hasTokenFallback = !!token && networkError;
    const effectiveAuth = isAuthenticated || hasTokenFallback;
    
    console.log('[CapacitorInitialRouteFix] Boot check:', { currentPath, effectiveAuth });
    
    // Only redirect on invalid initial paths (Capacitor cold-start edge cases)
    const needsRedirect = !currentPath || currentPath === '/index.html' || currentPath === '/index';
    
    if (needsRedirect) {
      const target = effectiveAuth ? '/' : '/login';
      console.log('[CapacitorInitialRouteFix] Redirecting from', currentPath, 'to', target);
      navigate(target, { replace: true });
    }
    // If already on a valid path (/, /night-mode, /therapy, etc.) — do nothing
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return children;
};

// Main App wrapper
// Main App wrapper
function App() {
  const [showIntroIdentity, setShowIntroIdentity] = useState(() => {
  return shouldShowHearenIntro();
});

  return (
    <>
      {showIntroIdentity && (
        <HearenIntroIdentity
          onComplete={() => setShowIntroIdentity(false)}
        />
      )}

      <BrowserRouter>
        <AuthProvider>
          <FullscreenProvider>
            <NowPlayingProvider>
              <CapacitorInitialRouteFix>
                <AppContent />
              </CapacitorInitialRouteFix>
            </NowPlayingProvider>

            <Toaster position="top-center" richColors />
            {/* <VersionMarker /> */}
          </FullscreenProvider>
        </AuthProvider>
      </BrowserRouter>
    </>
  );
}

export default App;